<script setup lang="ts">
// icons
import { faChevronCircleDown, faChevronCircleUp } from '@fortawesome/pro-regular-svg-icons';

// utils
import { nl2br, isInViewport, getOffsetTop } from '@/utils/general';

/********************
 * PROPS & EMITS     *
 ********************/
export interface CiReadMoreProps {
  charTolerance?: number;
  iconColor?: string;
  language?: string | null;
  usedLanguage?: string | null;
  maxChars?: number;
  placeholder?: string;
  text: string;
  textColor?: string;
}
const props = withDefaults(defineProps<CiReadMoreProps>(), {
  charTolerance: 150,
  iconColor: 'fill-dark-green',
  language: null,
  usedLanguage: null,
  maxChars: 300,
  placeholder: 'No text.',
  textColor: 'text-dark-green',
});

/********************
 * COMPOSITIONS      *
 ********************/
const { $gettext } = useGettext();

/********************
 * REFS & VARS       *
 ********************/
const buttonRef = useTemplateRef('buttonRef');
const isReadMore = ref(false);
const offsetBeforeCollapse = ref(null);

const formattedString = computed(() => {
  return getFormattedString();
});

const stringDifference = computed(() => {
  const formatted = getFormattedString(false);
  if (formatted !== props.text) {
    return nl2br(props.text.substr(formatted.length - 3));
  }
  return '';
});

const i18nButtonText = computed(() => {
  return isReadMore.value ? $gettext('Read less') : $gettext('Read more');
});

const textLanguage = computed(() => {
  return props.usedLanguage || props.language || undefined;
});

/********************
 * FUNCTIONS         *
 ********************/

function toggleReadMore(value: boolean) {
  isReadMore.value = value;

  // after collapsed
  if (!isReadMore.value) {
    // wait until collapsed
    nextTick(() => {
      if (buttonRef.value && !isInViewport(buttonRef.value.$el)) {
        window.scroll({ top: getOffsetTop(buttonRef.value.$el) - (offsetBeforeCollapse.value || 0) });
      }
    });
  }
}

function getFormattedString(convertNewlines = true) {
  let newText = props.text;
  if (!isReadMore.value && props.text.length > props.maxChars + props.charTolerance) {
    newText = props.text.substring(0, props.maxChars) + '...';
    let i = 1;
    while (
      props.maxChars + i <= props.text.length
      && props.text.substring(props.maxChars + i - 1, props.maxChars + i).match(/\S+/)
    ) {
      newText = props.text.substring(0, props.maxChars + i) + '...';
      i += 1;
    }
  }
  if (convertNewlines) {
    return nl2br(newText);
  }
  return newText;
}
</script>

<template>
  <div class="read-more">
    <span v-if="formattedString !== ''" :lang="textLanguage" v-html="formattedString" />
    <p v-else>
      {{ props.placeholder }}
    </p>
    <span
      v-if="stringDifference"
      :lang="textLanguage"
      class="hidden"
      v-html="stringDifference"
    />
    <span v-show="props.text.length > props.maxChars + props.charTolerance" class="whitespace-nowrap px-1 font-bold">
      <CiButton
        ref="buttonRef"
        tag="button"
        size="small"
        variant="text"
        :class="`inline-flex flex-row items-center ${props.textColor}`"
        @click.prevent="toggleReadMore(!isReadMore)"
      >
        {{ i18nButtonText }}
        <CiAwesomeIcon
          :class="`${props.iconColor} ml-2`"
          ratio="0.8"
          :icon="isReadMore ? faChevronCircleUp : faChevronCircleDown"
        />
      </CiButton>
    </span>
  </div>
</template>
